import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { renderThrough, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The bar a storefront wears above every page: a brand mark, a search field,
 * and the row of affordances a shopper reaches for — comparison, favourites,
 * an account, a basket.
 *
 *   <NavbarUtilities variant="centred">
 *     <NavbarUtilitiesBrand>
 *       <Link href={home}>{logo}</Link>
 *     </NavbarUtilitiesBrand>
 *
 *     <NavbarUtilitiesSearch>{search}</NavbarUtilitiesSearch>
 *
 *     <NavbarUtilitiesActions>
 *       <NavbarUtility render={<Link href={cart} />}>
 *         {cartMark}
 *         <NavbarUtilityCount aria-label={countLabel}>{count}</NavbarUtilityCount>
 *       </NavbarUtility>
 *       <NavbarAvatar render={<Link href={account} />}>{initials}</NavbarAvatar>
 *     </NavbarUtilitiesActions>
 *   </NavbarUtilities>
 *
 * **The utility item is one shape with a count that hangs off it.** A basket
 * with items, a basket without, a comparison, a favourites heart and a way into
 * an account are five spellings of the same thing: an affordance carrying a
 * mark, sometimes with a number overlaid. The count is a part with its own
 * recipe over a positioned frame — never a `className` a call site hands in —
 * and an item with nothing to count renders the same item without it, rather
 * than a different item.
 *
 * **What the mark is, and what pressing it does, are the tenant's.** The item
 * takes an icon as children and its element through `render`, so a basket that
 * navigates is a link and a sign-in that opens an overlay is a button, from one
 * part. No count, no route, no label and no atom crosses into this package.
 *
 * Server-first throughout: every part here is a plain component, and whatever
 * interactivity a tenant needs sits in the element it renders through.
 */

/**
 * The row itself. Renders the band and the run inside it, because a
 * composition that omitted the inner run would still render — with the search
 * field's `order-last` wrap silently resolving against the wrong container.
 */
const navbarUtilitiesClasses = 'lap:flex-row lap:items-center flex min-w-0 flex-1 flex-col';

/**
 * The run the slots actually sit in.
 *
 * Below `lap` there is one shape and it is the base: a wrapping row, where the
 * search field's `order-last` drops it onto a line of its own under the
 * brand/actions pair. What the appearance decides is what happens from `lap`
 * up, and the two brands on this platform answer it differently.
 *
 * The axis is the row's own layout rather than which slots a composition puts
 * in it — a bar with a support block beside its affordances and a bar without
 * one are the same two arrangements.
 */
const navbarUtilitiesRowVariants = cva('flex w-full flex-wrap items-center', {
  variants: {
    variant: {
      /**
       * The field floats. Brand and actions each take the slack, so the field
       * sits between them wherever their content leaves room, and the run
       * carries its own spacing because nothing measures the gaps for it.
       */
      centred: 'lap:flex-nowrap lap:gap-10 des:gap-20',
      /**
       * Evenly spread. The slots take their natural widths, the first is flush
       * to the start and the last to the end, and what is left over is divided
       * equally between them — so a bar carrying four things reads as four
       * things at even intervals rather than as content pushed to the edges.
       *
       * Not a column grid: equal columns leave a slot narrower than its column
       * sitting in a hole, and the hole grows with the viewport. Even gaps
       * between content-sized slots is what the arrangement actually is, and it
       * has no width at which anything has to be dropped to make it fit.
       *
       * A composition using this states which of its slots may take the slack,
       * because the slot recipes are written for `centred`, where the brand and
       * the affordance row both grow.
       */
      spread: 'lap:flex-nowrap lap:justify-between lap:gap-6',
    },
  },
});

/** Required and closed — not `VariantProps`, which makes it optional. */
export type NavbarUtilitiesVariantProps = {
  /** `null` keeps the wrapping row at every width, emitting no step at `lap`. */
  readonly variant: 'centred' | 'spread' | null;
};

export type NavbarUtilitiesProps = ComponentProps<'header'> & NavbarUtilitiesVariantProps;

/** The utilities bar. */
export const NavbarUtilities = ({
  className,
  children,
  variant,
  ...props
}: NavbarUtilitiesProps) => (
  <header
    data-slot="navbar-utilities"
    className={clsxm(navbarUtilitiesClasses, className)}
    {...props}
  >
    <div data-slot="navbar-utilities-row" className={navbarUtilitiesRowVariants({ variant })}>
      {children}
    </div>
  </header>
);

/**
 * The brand slot. Fixed heights rather than padding: the bar is the same height
 * whether or not a tenant hangs anything beside the mark.
 */
const navbarUtilitiesBrandClasses = 'lap:h-[93px] flex h-[54px] flex-1 items-center gap-2';

export type NavbarUtilitiesBrandProps = ComponentProps<'div'>;

/** Where the brandmark sits. */
export const NavbarUtilitiesBrand = ({ className, ...props }: NavbarUtilitiesBrandProps) => (
  <div
    data-slot="navbar-utilities-brand"
    className={clsxm(navbarUtilitiesBrandClasses, className)}
    {...props}
  />
);

/**
 * The search slot. One field, placed by layout rather than duplicated per
 * breakpoint: below `lap` the run wraps and `order-last` drops the field onto
 * its own line under the brand/actions pair; from `lap` up the run stops
 * wrapping and the field takes its natural middle place.
 */
const navbarUtilitiesSearchClasses =
  'lap:order-none lap:mb-0 lap:flex lap:items-center lap:justify-center order-last mb-5 w-full';

export type NavbarUtilitiesSearchProps = ComponentProps<'div'>;

/** Where the search field sits. */
export const NavbarUtilitiesSearch = ({ className, ...props }: NavbarUtilitiesSearchProps) => (
  <div
    data-slot="navbar-utilities-search"
    className={clsxm(navbarUtilitiesSearchClasses, className)}
    {...props}
  />
);

/** The trailing end of the bar, matching the brand slot's height. */
const navbarUtilitiesActionsClasses =
  'lap:h-[93px] lap:w-full flex h-[54px] flex-1 items-center justify-end';

/** The affordances' own spacing, which the `<nav>` around them does not set. */
const navbarUtilitiesActionsRowClasses = 'flex items-center gap-6';

export type NavbarUtilitiesActionsProps = ComponentProps<'nav'>;

/** The row of affordances at the trailing end. */
export const NavbarUtilitiesActions = ({
  className,
  children,
  ...props
}: NavbarUtilitiesActionsProps) => (
  <nav
    data-slot="navbar-utilities-actions"
    className={clsxm(navbarUtilitiesActionsClasses, className)}
    {...props}
  >
    <div data-slot="navbar-utilities-actions-row" className={navbarUtilitiesActionsRowClasses}>
      {children}
    </div>
  </nav>
);

/**
 * One affordance in that row.
 *
 * `relative` is load-bearing rather than decorative: it is the frame the count
 * is positioned against, and an item composed without it would hang its count
 * off whatever ancestor happened to be positioned. So the item carries it
 * whether or not a count is composed into it — the same item, with or without
 * a number.
 *
 * The mark keeps its own size. The five marks this row carries step
 * differently today (24→28, 28→32, 24→32), and a recipe that sized them here
 * would move three of them; converging them is a design decision rather than
 * an extraction, and is recorded as such.
 */
const navbarUtilityClasses = 'relative flex cursor-pointer items-center';

export type NavbarUtilityProps = ComponentProps<'button'> & {
  /**
   * The element to paint instead of the neutral `<button>` — a link where the
   * affordance is a destination rather than an overlay.
   */
  readonly render?: RenderElement;
};

/**
 * An affordance in the utilities row.
 *
 * The neutral element is a `<button>` with `type="button"`: an affordance that
 * opens an overlay is a control, and one that goes somewhere arrives through
 * `render` as the tenant's own link.
 */
export const NavbarUtility = ({ className, render, type, ...props }: NavbarUtilityProps) => {
  const resolved = clsxm(navbarUtilityClasses, className);

  return (
    renderThrough(render, 'navbar-utility', resolved, props) ?? (
      <button data-slot="navbar-utility" type={type ?? 'button'} className={resolved} {...props} />
    )
  );
};

/**
 * The number overlaid on an affordance.
 *
 * Positioned against the item's own frame rather than dissolved into it: the
 * badge overhangs the mark's top-leading corner and rings itself in the bar's
 * ground so it stays legible over whatever the mark draws underneath.
 */
const navbarUtilityCountClasses =
  'bg-primary text-primary-foreground ring-background lap:left-5 lap:px-2 lap:text-sm absolute -top-2 left-4 rounded-full px-1.5 text-xs font-bold ring-2';

export type NavbarUtilityCountProps = ComponentProps<'span'>;

/** The count overlaid on an affordance. */
export const NavbarUtilityCount = ({ className, ...props }: NavbarUtilityCountProps) => (
  <span
    data-slot="navbar-utility-count"
    className={clsxm(navbarUtilityCountClasses, className)}
    {...props}
  />
);

/**
 * The account mark for a shopper who is signed in: a disc carrying whatever
 * stands for them. Not a `NavbarUtility` — it has no mark to hold, because it
 * IS the mark, and nothing is ever counted on it.
 *
 * The initials are the tenant's: deriving them from a name, an e-mail or
 * neither is that shop's rule, and the disc paints whatever it is handed.
 */
const navbarAvatarClasses =
  'font-heading bg-primary/20 lap:h-8 lap:w-8 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold';

export type NavbarAvatarProps = ComponentProps<'span'> & {
  /** The element to paint instead of the neutral `<span>` — usually a link. */
  readonly render?: RenderElement;
};

/** The signed-in account mark. */
export const NavbarAvatar = ({ className, render, ...props }: NavbarAvatarProps) => {
  const resolved = clsxm(navbarAvatarClasses, className);

  return (
    renderThrough(render, 'navbar-avatar', resolved, props) ?? (
      <span data-slot="navbar-avatar" className={resolved} {...props} />
    )
  );
};
